import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { copy } from '@shakkerai/util-copy';
import { notifyError, notifySuccess } from '@shakkerai/pro-components';
import { applyReferenceImage, applyGenerateParams, saveImageToBestPosition } from '@shakkerai/pro-utils';
import {
  postCreateAblum,
  postEditAblum,
  postDeleteAblum,
  deleteGenHistoryItem,
  collectGenHistoryItem,
  cancelCollectGenHistoryItem,
  requestTaskDetail,
} from '@shakkerai/services';
// import { CollectSuccessContent } from '../components/CollectSuccessContent';
import { translate } from '@shakkerai/util-i18n';
import { encode } from '@shakkerai/pro-utils';
import { mc } from '@shakkerai/report';
import { useSelectDeal } from '../store/base';
import { eventBus } from '@starflow/app/helpers';

import {
  EVENT_LABEL_UNCOLLECTED,
  EVENT_LABEL_COLLECTED,
  EVENT_LABEL_DELETE,
  EVENT_LABEL_BATCH_COLLECTED,
  EVENT_LABEL_BATCH_UNCOLLECTED,
  EVENT_LABEL_BATCH_DELETED,
} from '../utils/eventbus';
import { shareToCommunity } from '../utils/share';
import { useDownloadState } from '@shakkerai/pro-store';
import { getRemoteImageSize } from '@shakkerai/util-image';

/**
 * 解析图片详情
 *
 * @param {*} itemInfo
 * @returns
 */
const useResolveImageDetailHooks = (itemInfo) => {
  // imageDetail中存放了本次训练任务的参数，接口结构：https://app.apifox.com/project/2950219
  const imageDetail = useRef(new Map());

  const resolveImageDetail = useCallback(async () => {
    if (imageDetail.current.has(itemInfo?.generateId)) {
      return Promise.resolve(imageDetail.current.get(itemInfo?.generateId));
    } else {
      const responseData = await requestTaskDetail({
        generateId: itemInfo?.generateId,
      });

      if (responseData.code === 0) {
        imageDetail.current.set(itemInfo?.generateId, responseData.data);
        return responseData.data;
      } else {
        throw new Error(translate('parameters_import_failed'));
      }
    }
  }, [itemInfo?.generateId]);

  return {
    resolveImageDetail,
  };
};

/**
 * 单个图片
 * @param {*} props
 * @returns
 */
export const useGenHistoryItemEventHooks = (props) => {
  const { onCollectDone, onDeleteDone, itemInfo, source = '' } = props;
  const [isSelected, setSelected] = useState(false);

  /**
   * 用于检查收到的事件是否与当前的图片匹配
   */
  const checkMatch = useCallback(
    (matchCallback, ...args) => {
      return (eventImageId) => {
        if (eventImageId === itemInfo?.outputId) {
          matchCallback && matchCallback(...args);
        }
      };
    },
    [itemInfo],
  );

  useEffect(() => {
    const collectWrapper = checkMatch(onCollectDone, true);
    const uncollectWrapper = checkMatch(onCollectDone, false);
    const deleteWrapper = checkMatch(onDeleteDone);

    eventBus.on(EVENT_LABEL_COLLECTED, collectWrapper);
    eventBus.on(EVENT_LABEL_UNCOLLECTED, uncollectWrapper);
    eventBus.on(EVENT_LABEL_DELETE, deleteWrapper);

    return () => {
      eventBus.off(EVENT_LABEL_COLLECTED, collectWrapper);
      eventBus.off(EVENT_LABEL_UNCOLLECTED, uncollectWrapper);
      eventBus.off(EVENT_LABEL_DELETE, deleteWrapper);
    };
  }, [checkMatch, onCollectDone, onDeleteDone]);

  /**
   * 是否NSFW内容
   */
  const [isNSFWContent, setIsNSFWContent] = useState(itemInfo?.isNSFW || false);

  const { resolveImageDetail } = useResolveImageDetailHooks(itemInfo);
  /**
   * 收藏
   */
  const onCollect = useCallback(async () => {
    const responseData = await collectGenHistoryItem({
      outputIds: [itemInfo.outputId],
    });

    if (responseData.code === 0) {
      // notifySuccess(``, CollectSuccessContent(responseData.data.groupName));
      eventBus.emit(EVENT_LABEL_COLLECTED, itemInfo.outputId);
      mc('tool.history.collect', {
        image_uuid: itemInfo.outputId,
      });
    }
  }, [itemInfo]);

  /**
   * 取消收藏
   */
  const onCancelCollect = useCallback(async () => {
    const responseData = await cancelCollectGenHistoryItem({
      outputIds: [itemInfo.outputId],
    });

    if (responseData.code === 0) {
      eventBus.emit(EVENT_LABEL_UNCOLLECTED, itemInfo.outputId);
      // notifySuccess(`${translate('collect_cancel_toast')}`);
    }
  }, [itemInfo]);

  /**
   * 删除
   */
  const onDelete = useCallback(async () => {
    // 已通
    const responseData = await deleteGenHistoryItem({
      outputId: itemInfo.outputId,
    });

    if (responseData.code === 0) {
      eventBus.emit(EVENT_LABEL_DELETE, itemInfo.outputId);
      notifySuccess(translate('delete_img_toast'));
    }
  }, [itemInfo]);

  const { downloadImageWithConfirm } = useDownloadState();

  /**
   * 下载
   */
  const onDownload = useCallback(async () => {
    downloadImageWithConfirm(itemInfo.imageId, itemInfo.outputId);
  }, [itemInfo, downloadImageWithConfirm]);

  /**
   * 导入生图参数
   */
  const onImport = useCallback(async () => {
    try {
      const imageDetail = await resolveImageDetail();

      const { projectData } = imageDetail.taskParam;
      const { seed } = itemInfo;

      projectData.seedType = '0';
      projectData.seedNumber = seed;

      await applyGenerateParams(projectData, {
        ...imageDetail.taskParam,
        styleIcon: itemInfo.imageId,
      });
      notifySuccess(translate('history_apply_toast'));

      mc('tool.history.detail.import', {
        image_uuid: itemInfo.outputId,
        area: source,
      });
    } catch (error) {
      notifyError(translate('parameters_import_failed'));
    }
  }, [resolveImageDetail, itemInfo, source]);

  /**
   * 添加为参考图
   */
  const onAddAsReference = useCallback(() => {
    applyReferenceImage({ url: itemInfo?.imageId, width: itemInfo?.width, height: itemInfo?.height });

    mc('tool.history.detail.reference', {
      image_uuid: itemInfo.outputId,
      area: source,
    });
  }, [itemInfo, source]);

  /**
   * 将所引用的参考图设为参考图
   */
  const onAddReferenceAsReference = useCallback(async () => {
    try {
      const imageDetail = await resolveImageDetail();
      const { taskParam, referenceImage, images } = imageDetail || {};
      const { projectData } = taskParam || {};

      let width = images?.[0]?.width;
      let height = images?.[0]?.height;

      if (!projectData.reference?.image) {
        // 这种情况是局部重绘
        width = projectData.renderWidth || width;
        height = projectData.renderHeight || height;
      }

      let controlWeight = projectData.reference?.scale;

      if (typeof controlWeight !== 'number') {
        controlWeight = null;
      } else {
        controlWeight = Math.max(0.1, Math.min(1, controlWeight / 100));
      }

      applyReferenceImage({ url: referenceImage, controlWeight, width, height });
    } catch (error) {
      notifyError(translate('parameters_add_reference_failed'));
    }
  }, [resolveImageDetail]);

  /**
   * 添加至画布
   */
  const onAddToCanvas = useCallback(async () => {
    try {
      const imageDetail = await resolveImageDetail();

      const { imageId: url } = itemInfo;
      const { projectData } = imageDetail.taskParam;
      const { seed, outputId } = itemInfo;
      const { width, height } = await getRemoteImageSize(url);

      saveImageToBestPosition({ id: outputId, taskId: imageDetail.generateId, url, width, height, seed, projectData });

      mc('tool.history.detail.canvas', {
        image_uuid: itemInfo.outputId,
        area: source,
      });
    } catch (error) {
      console.error(error);
      notifyError(translate('parameters_add_to_canvas_failed'));
    }
  }, [itemInfo, resolveImageDetail, source]);

  /**
   * 复制Prompt
   */
  const onCopyPrompt = useCallback(async () => {
    try {
      const imageDetail = await resolveImageDetail();
      copy(imageDetail.param?.prompt).then(
        () => {
          notifySuccess(translate('copy_prompt_toast'));
        },
        () => {
          notifyError('Copy Failed');
        },
      );
    } catch (error) {
      notifyError('Copy Failed');
    }
  }, [resolveImageDetail]);

  /**
   * 复制负向Prompt
   */
  const onCopyNegativePrompt = useCallback(async () => {
    try {
      const imageDetail = await resolveImageDetail();
      copy(imageDetail.param?.negativePrompt).then(
        () => {
          notifySuccess(translate('copy_negprompt_toast'));
        },
        () => {
          notifyError('Copy Failed');
        },
      );
    } catch (error) {
      notifyError('Copy Failed');
    }
  }, [resolveImageDetail]);

  /**
   * 查看NSFW内容
   */
  const viewNSFWContent = useCallback(() => {
    setIsNSFWContent(false);
  }, []);

  /**
   * 分享，不需要事件，而是要通过SharePopover自动触发
   */
  const shareContent = useMemo(() => {
    const params = `g=${itemInfo?.generateId}&i=${itemInfo?.outputId}`;
    return `${window.location.origin}/aigenerator/share?p=${encode(params)}`;
  }, [itemInfo]);

  const onCopyShareLink = useCallback(() => {
    copy(shareContent).then(() => notifySuccess(translate('copied')));
  }, [shareContent]);

  /**
   * 分享到社区
   */
  const onShareToCommunity = useCallback(() => {
    shareToCommunity([itemInfo]);
  }, [itemInfo]);

  return {
    onCollect,
    onCancelCollect,
    onDelete,
    onDownload,
    onImport,
    onAddAsReference,
    onAddReferenceAsReference,
    onAddToCanvas,
    shareContent,
    onShareToCommunity,
    onCopyPrompt,
    onCopyNegativePrompt,
    onCopyShareLink,

    viewNSFWContent,

    isNSFWContent,
    isSelected,

    setSelected,
  };
};

/**
 * 专辑
 */
export const useAlbumHooks = () => {
  /**
   * 创建专辑
   */
  const createAblum = useCallback(async (ablumName) => {
    const response = await postCreateAblum({
      groupName: ablumName,
    });

    if (response.code === 0) {
      return response.data;
    } else {
      return response.msg;
    }
  }, []);

  /**
   * 重命名
   */
  const renameAblum = useCallback(async (groupId, ablumName) => {
    const response = await postEditAblum({
      groupName: ablumName,
      groupId: groupId,
    });

    if (response.code === 0) {
      return response.data;
    } else {
      return response.msg;
    }
  }, []);

  /**
   * 删除或清空
   */
  const deleteAblum = useCallback(async (groupId, clearImages = false) => {
    const response = await postDeleteAblum({
      groupId,
      clearImages,
    });

    if (response.code === 0) {
      return response.data;
    } else {
      return response.msg;
    }
  }, []);

  return {
    createAblum,
    deleteAblum,
    renameAblum,
  };
};

export const useBatchImageHooks = (eventCallback) => {
  const { selectedOutputId, seletedMap } = useSelectDeal();

  const deleteAll = useCallback(async () => {
    const responseData = await deleteGenHistoryItem({
      outputIds: selectedOutputId,
    });

    if (responseData.code === 0) {
      notifySuccess(translate('delete_img_toast'));
      eventCallback(EVENT_LABEL_BATCH_DELETED, selectedOutputId);
    }
  }, [selectedOutputId, eventCallback]);

  const downloadAll = useCallback(() => {
    // eslint-disable-next-line
  }, [selectedOutputId]);

  const collectAll = useCallback(async () => {
    const responseData = await collectGenHistoryItem({
      outputIds: selectedOutputId,
    });

    if (responseData.code === 0) {
      // notifySuccess(``, CollectSuccessContent(responseData.data.groupName));
      eventCallback(EVENT_LABEL_BATCH_COLLECTED, selectedOutputId);
    }
    // eslint-disable-next-line
  }, [selectedOutputId, eventCallback]);

  const uncollectALL = useCallback(async () => {
    const responseData = await cancelCollectGenHistoryItem({
      outputIds: selectedOutputId,
    });

    if (responseData.code === 0) {
      // notifySuccess(`已取消收藏`);
      eventCallback(EVENT_LABEL_BATCH_UNCOLLECTED, selectedOutputId);
    }
  }, [selectedOutputId, eventCallback]);

  const shareAll = useCallback(() => {
    shareToCommunity(Array.from(seletedMap.values()));
    // eslint-disable-next-line
  }, [selectedOutputId, seletedMap]);

  return {
    deleteAll,
    downloadAll,
    collectAll,
    uncollectALL,
    shareAll,
  };
};

/**
 * 新消息分发
 *
 * @returns
 */
export const useMessageTipHooks = (scrollRef, eventReceiveLabel, refreshPage) => {
  // eslint-disable-next-line
  const [showUpdateTips, setShowUpdateTips] = useState(false);
  const [newRecordCount, setNewRecordCount] = useState(0);

  const eventListener = useCallback(
    (count) => {
      const height = scrollRef.current?.getBoundingClientRect().height || 0;
      // scrollRef为空说明当前页面还未渲染列表
      if (scrollRef.current?.scrollTop < height / 2 || !scrollRef.current) {
        // 刷新
        if (count > 0) {
          refreshPage && refreshPage();
        }
      } else {
        if (count > 0) {
          // 展示消息提示
          setShowUpdateTips(true);
          setNewRecordCount(count);
        } else {
          setShowUpdateTips(false);
        }
      }
      // eslint-disable-next-line
    },
    [refreshPage, scrollRef],
  );

  useEffect(() => {
    eventBus.on(eventReceiveLabel, eventListener);
    return () => {
      eventBus.off(eventReceiveLabel, eventListener);
    };
  }, [eventReceiveLabel, eventListener]);

  const closeTip = useCallback(() => {
    setShowUpdateTips(false);
  }, []);

  return {
    showUpdateTips,
    newRecordCount,
    closeTip,
  };
};
